import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Button, Space, Avatar, Typography, Select, Row, Col, Modal, Tag, Alert, Collapse } from "antd";
import {
	SaveOutlined,
	FileTextOutlined,
	FilePdfOutlined,
	EditOutlined,
	ExclamationCircleOutlined,
	UndoOutlined,
} from "@ant-design/icons";
import { format } from "date-fns";
import { Input } from "antd";
import { getSessionUser } from "../../../utils/auth/sessionUser";
import {
	updateAssignedForm,
	getFormResults,
	updateFormResponse,
	getFormResponseRevisions,
	reopenAssignedForm,
	AssignedForm,
	FieldResponse,
	FormResponse,
	FormResponseRevision,
	FormElement,
	FormSection,
} from "../../../api/formApi";
import { allowedStatusOptions, canTransitionTo, canReopen } from "./formStatusTransitions";
import DetailDrawer from "../../../components/shared/DetailDrawer";
import SectionCard from "../../../components/shared/SectionCard";
import StatusTag from "../../../components/shared/StatusTag";
import UnifiedFormRenderer, { flattenFields } from "../../../components/forms/FormRenderer";
import { normaliseFieldType, toSingleValue, toMultiValue } from "../../../components/forms/fieldTypes";
import { useNotifications } from "../../../context/NotificationContext";
import type { UserUuid } from "../../../types/identity";

const { TextArea } = Input;
const { Text } = Typography;

// Extend FieldResponse locally to carry options for checkbox/radio rendering
interface RichFieldResponse extends FieldResponse {
	options?: string[];
}

/**
 * Human wording for `form_responses.submitted_via`. A NULL is absent on purpose.
 *
 * Each entry reads as the tail of "Submitted by … via …", so it is a noun phrase, not a
 * participle. `import` (ClickUp 86d418z6b / S7.1) deliberately names no source system — the
 * stored value is the generic `import` and inventing "Cliniko" here would put a system name
 * on the screen that the row never claimed.
 */
const SUBMITTED_VIA_LABELS: Record<string, string> = {
	patient_link: "patient link",
	practitioner_on_behalf: "a practitioner on the patient's behalf",
	handover_device: "handover device",
	import: "a data import from another system",
};

/**
 * Normalise every shape a stored/served answer set can arrive in into a field-id → value map.
 *
 * FOUR shapes reach this, and dropping any of them shows a blank form over real answers:
 *   • a JSON string of a FieldResponse[]  — `form_responses.response` (the common case)
 *   • a JSON string of a plain map        — older rows
 *   • an ALREADY-DECODED FieldResponse[]  — an F4 revision snapshot, which the controller
 *     stores decoded so it round-trips as answers rather than as a string
 *   • an already-decoded plain map
 */
const resolveResponseMap = (raw: any): Record<string, any> => {
	if (!raw) return {};
	if (Array.isArray(raw)) {
		return raw.reduce((acc: Record<string, any>, f: FieldResponse) => {
			if (f && f.id !== undefined) acc[f.id] = f.response;
			return acc;
		}, {});
	}
	if (typeof raw === "object") return raw;
	if (typeof raw === "string") {
		try {
			return resolveResponseMap(JSON.parse(raw));
		} catch {
			return {};
		}
	}
	return {};
};

/**
 * The stored FieldResponse entries keyed by field id, when the response was stored in that
 * shape. Used to preserve each entry's recorded `question` and `type` through an edit: the
 * form TEMPLATE may have been reworded since submission, and an unrelated answer correction
 * must not silently rewrite the question the patient actually answered.
 */
const resolveStoredEntries = (raw: any): Record<string, FieldResponse> => {
	const decoded = typeof raw === "string" ? (() => { try { return JSON.parse(raw); } catch { return null; } })() : raw;
	if (!Array.isArray(decoded)) return {};
	return decoded.reduce((acc: Record<string, FieldResponse>, f: FieldResponse) => {
		if (f && f.id !== undefined) acc[f.id] = f;
		return acc;
	}, {});
};

interface ReviewFormModalProps {
	isOpen: boolean;
	onClose: () => void;
	onSave: () => void;
	assignedForm: AssignedForm;
}

const ReviewFormModal: React.FC<ReviewFormModalProps> = ({ isOpen, onClose, onSave, assignedForm }) => {
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [isReopening, setIsReopening] = useState(false);
	const [isExporting, setIsExporting] = useState(false);
	const [notes, setNotes] = useState<string>("");
	const [newStatus, setNewStatus] = useState<string>("Reviewed");
	const [formResponse, setFormResponse] = useState<FormResponse | null>(null);
	const [responseMap, setResponseMap] = useState<Record<string, any>>({});

	// ─── F9 edit mode ─────────────────────────────────────────────────────────────
	// `responseMap` stays the PRISTINE stored answers throughout an edit. `editValues` is
	// the working copy, so Cancel is a discard rather than a reload, and the signature
	// preservation below always has an untouched original to copy from.
	const [isEditing, setIsEditing] = useState(false);
	const [editValues, setEditValues] = useState<Record<string, any>>({});
	const [storedEntries, setStoredEntries] = useState<Record<string, FieldResponse>>({});
	const [isSavingResponse, setIsSavingResponse] = useState(false);
	const [responseValid, setResponseValid] = useState(true);
	const [revisions, setRevisions] = useState<FormResponseRevision[]>([]);

	const { showError, showSuccess } = useNotifications();

	/** Current user, for resolving "you" in the provenance line without inventing a name. */
	const currentUser = useMemo<any>(() => {
		try { return getSessionUser(); } catch { return {}; }
	}, []);

	// Parsed form structure, fed to the read-only shared renderer.
	const parsedFormStructure = useMemo<(FormElement | FormSection)[]>(() => {
		const raw = assignedForm.form?.structure;
		if (!raw) return [];
		try {
			return typeof raw === "string" ? JSON.parse(raw) : raw;
		} catch {
			return [];
		}
	}, [assignedForm.form]);

	// ─── Build FieldResponse[] from form structure + response map ────────────────
	const buildStructure = useCallback(
		(responseMap: Record<string, any>): RichFieldResponse[] => {
			if (!assignedForm.form) return [];

			let rawStructure = assignedForm.form.structure;
			let parsed: FormElement[] | FormSection[];

			try {
				parsed = typeof rawStructure === "string" ? JSON.parse(rawStructure) : rawStructure;
			} catch (e) {
				console.error("Failed to parse form structure", e);
				return [];
			}

			const result: RichFieldResponse[] = [];

			const processField = (field: FormElement): RichFieldResponse => ({
				id: field.id,
				type: field.type,
				// paragraph fields store their content in placeholder, not question.
				// No `|| field.label` tail — that is a builder nickname defaulting to the
				// palette entry, and it must not caption a clinical record.
				question: field.question || (field as any).placeholder || "",
				response: responseMap[field.id] ?? null,
				options: field.options,
			});

			if (assignedForm.form.use_sections) {
				(parsed as FormSection[]).forEach((section) => {
					// Inject a heading row for each section
					result.push({
						id: `__section__${section.id}`,
						type: "heading",
						question: section.title,
						response: null,
					});
					section.fields.forEach((f) => result.push(processField(f)));
				});
			} else {
				(parsed as FormElement[]).forEach((f) => result.push(processField(f)));
			}

			return result;
		},
		[assignedForm.form]
	);

	/** Adopt a freshly fetched/returned response row as the new pristine state. */
	const applyResponseRow = useCallback((row: FormResponse) => {
		const map = resolveResponseMap(row.response);
		setResponseMap(map);
		setStoredEntries(resolveStoredEntries(row.response));
		setFormResponse({ ...row, structure: buildStructure(map) });
		return row;
	}, [buildStructure]);

	/**
	 * Only when the row says a history exists. `edit_count` is the newest `revision_number`,
	 * so 0 proves the list is empty — and this modal opens from six places, each of which
	 * would otherwise fire a request and a `form.viewed` audit row to be told "[]".
	 */
	const loadRevisions = useCallback(async (row: FormResponse | null) => {
		if (!row || !row.edit_count) {
			setRevisions([]);
			return;
		}
		try {
			setRevisions(await getFormResponseRevisions(assignedForm.id));
		} catch (error) {
			// The history is context, not the record — failing to load it must not break the
			// review surface the other five call sites depend on.
			console.error("Failed to load form response revisions:", error);
		}
	}, [assignedForm.id]);

	const loadFormResponse = useCallback(async () => {
		try {
			const data = await getFormResults(assignedForm.id);
			return applyResponseRow(data);
		} catch (error) {
			console.error("Failed to load form response:", error);
			return null;
		}
	}, [assignedForm.id, applyResponseRow]);

	useEffect(() => {
		if (!assignedForm) return;

		setNotes(assignedForm.notes || "");
		// A newly opened assignment is never mid-edit, whichever call site opened it.
		setIsEditing(false);
		setEditValues({});
		setResponseValid(true);
		setRevisions([]);

		// Open on the move the practitioner most often wants — sign-off — but ONLY where the
		// backend will take it. This used to preselect "Reviewed" for Pending as well as
		// Completed, and `Pending -> Reviewed` is not in S2's matrix (nothing has been submitted
		// yet to review), so the FIRST save of a pending row 422'd — a notes-only save included,
		// because `handleSave` sends `status` on every save. Everything else opens on the current
		// value, which is always legal as a self-transition.
		setNewStatus(canTransitionTo(assignedForm.status, "Reviewed") ? "Reviewed" : assignedForm.status);

		if (assignedForm.response?.response) {
			// Response already embedded in the assignedForm — build structure directly
			loadRevisions(applyResponseRow(assignedForm.response));
		} else {
			loadFormResponse().then(loadRevisions);
		}
	}, [assignedForm]); // eslint-disable-line react-hooks/exhaustive-deps

	const handleSave = useCallback(async () => {
		setIsSubmitting(true);
		try {
			await updateAssignedForm(assignedForm.id, {
				status: newStatus,
				notes,
				reviewed_at: new Date(),
			});
			showSuccess("Form updated successfully");
			onSave();
		} catch (error: any) {
			console.error("Failed to update form:", error);
			showError(error.response?.data?.message || "Failed to update form. Please try again.");
		} finally {
			setIsSubmitting(false);
		}
	}, [assignedForm, notes, newStatus, onSave, showError, showSuccess]);

	// ═══ TRANSITION GUARDING — WHAT THE PICKER IS ALLOWED TO OFFER ═══════════════
	//
	// The dropdown used to offer all five values from every status, `Completed -> Pending`
	// included — which is how a sign-off could be undone by a practitioner who thought they
	// were saving a note. S2 refuses the illegal ones on the PUT; this only stops the tray
	// OFFERING a move that would come back a 422. It is UX, not the guard.
	//
	// The set always contains `assignedForm.status` itself: `handleSave` sends `status` on
	// every save including a notes-only one, so dropping the current value would start failing
	// notes edits. See `formStatusTransitions.ts` for the matrix and why X -> X is in it.
	const statusOptions = useMemo(() => allowedStatusOptions(assignedForm.status), [assignedForm.status]);

	const performReopen = useCallback(async () => {
		setIsReopening(true);
		try {
			await reopenAssignedForm(assignedForm.id);
			showSuccess("Form reopened — it is back to Pending and can be submitted again");
			// The same refresh path `handleSave` uses. Most call sites close this drawer on
			// `onSave()`, so the practitioner returns to a refreshed list; where one keeps it
			// open, the status picker and this button re-evaluate against the new status.
			onSave();
		} catch (error: any) {
			console.error("Failed to reopen form:", error);
			showError(error.response?.data?.message || "Failed to reopen form. Please try again.");
		} finally {
			setIsReopening(false);
		}
	}, [assignedForm.id, onSave, showError, showSuccess]);

	const handleReopen = useCallback(() => {
		Modal.confirm({
			title: "Reopen this form?",
			icon: <ExclamationCircleOutlined />,
			content:
				"This returns the form to Pending so it can be submitted again, undoing the sign-off "
				+ "on this assignment. The submitted answers are kept, and the reopen is recorded "
				+ "against your name.",
			okText: "Reopen form",
			cancelText: "Cancel",
			// Undoing a sign-off is a deliberate act, not something a stray backdrop click or a
			// reflexive Escape should be able to confirm — same rule as the answer replace above.
			maskClosable: false,
			keyboard: false,
			onOk: () => performReopen(),
		});
	}, [performReopen]);

	// ═══ F9 — EDIT THE SUBMITTED RESPONSE ════════════════════════════════════════
	//
	// A DIFFERENT ENDPOINT WITH DIFFERENT CONSEQUENCES from `handleSave` above.
	// `handleSave` → PUT /practitioner/forms/assigned/{id}  — status, notes, reviewed_at.
	// This       → PUT /practitioner/forms/results/{id}     — the patient's ANSWERS.
	// They are never dispatched together, and while editing, each save button sits with the
	// fields it writes and names its own scope in its label.

	const useSections = !!assignedForm.form?.use_sections;

	/**
	 * ═══ THE PATIENT'S SIGNATURE IS NOT THE PRACTITIONER'S TO CHANGE ═════════════
	 * Editing a form containing a signature means altering a signed document. F8 blocks a
	 * practitioner from CREATING one; F9 blocks them from altering one that exists, while
	 * still showing it — a practitioner correcting a text answer must be able to see the
	 * attestation they are leaving intact.
	 *
	 * Two independent guarantees, because either alone is thin:
	 *   1. RENDER — the field id goes to `readOnlyFieldIds`, keeping it on
	 *      FormFieldRenderer's read-only `<img>` branch. This is also why the whole form
	 *      cannot simply be flipped editable: the editable `signature` branch offers a
	 *      "Clear" button that nulls the patient's mark. (Since 86d3y4ea7 that branch does
	 *      at least PAINT an existing signature rather than mounting blank — but being able
	 *      to see it was never the point here; not being able to replace it is.)
	 *   2. PAYLOAD — `buildResponsePayload` copies the signature from the pristine
	 *      `responseMap`, never from `editValues`. Whatever ends up in the working copy, the
	 *      bytes that go back to healthcare are the bytes that came out of it.
	 */
	const signatureFieldIds = useMemo<Set<string | number>>(
		() => new Set(
			flattenFields(parsedFormStructure, useSections)
				.filter((f) => normaliseFieldType(f.type) === "signature")
				.map((f) => f.id),
		),
		[parsedFormStructure, useSections],
	);

	/**
	 * Identical to the stored structure except that signature fields are no longer
	 * `required`. They are already answered, so this normally changes nothing — but a
	 * required signature the practitioner is forbidden to fill must never become a gate they
	 * cannot pass (F8's deadlock rule, applied to a save button instead of a wizard).
	 */
	const editStructure = useMemo<(FormElement | FormSection)[]>(() => {
		if (signatureFieldIds.size === 0) return parsedFormStructure;
		const relax = (f: FormElement): FormElement =>
			signatureFieldIds.has(f.id) ? { ...f, required: false } : f;
		return useSections
			? (parsedFormStructure as FormSection[]).map((s) => ({ ...s, fields: (s.fields ?? []).map(relax) }))
			: (parsedFormStructure as FormElement[]).map(relax);
	}, [parsedFormStructure, useSections, signatureFieldIds]);

	/**
	 * The array-of-FieldResponse shape every other write site uses, so an edited row reads
	 * back identically to a submitted one (`responseJson()`'s two-shape contract, the PDF
	 * export, and `QuestionnaireDataSource::formatForPrompt()`).
	 */
	const buildResponsePayload = useCallback((): FieldResponse[] => {
		const out: FieldResponse[] = [];
		for (const field of flattenFields(parsedFormStructure, useSections)) {
			const canonical = normaliseFieldType(field.type);
			if (canonical === "heading" || canonical === "paragraph") continue;

			const original = storedEntries[field.id as string];
			const value = signatureFieldIds.has(field.id)
				? (responseMap[field.id] ?? null)   // byte-preserved; see the block comment above
				: (editValues[field.id] ?? null);

			out.push({
				id: field.id,
				// Prefer what was RECORDED at submission over the current template, so a
				// reworded question does not retroactively rewrite the patient's record.
				type: original?.type ?? field.type,
				question: original?.question ?? field.question ?? "",
				response: value,
			});
		}
		return out;
	}, [parsedFormStructure, useSections, storedEntries, signatureFieldIds, responseMap, editValues]);

	const handleEnterEdit = useCallback(() => {
		setEditValues({ ...responseMap });
		setResponseValid(true);
		setIsEditing(true);
	}, [responseMap]);

	const handleCancelEdit = useCallback(() => {
		// A discard, not a reload — `responseMap` was never touched.
		setEditValues({});
		setResponseValid(true);
		setIsEditing(false);
	}, []);

	const performSaveResponse = useCallback(async () => {
		setIsSavingResponse(true);
		try {
			const updated = await updateFormResponse(assignedForm.id, JSON.stringify(buildResponsePayload()));
			// F4 answers the PUT with the updated row itself — answers, `edit_count`,
			// `last_edited_by_user_id` and `last_edited_at` all present. That IS the refetch,
			// so there is deliberately no follow-up GET: a second request could only add a
			// `form.viewed` audit row and a window in which a stale read overwrites the value
			// we were just handed. The revisions DO need their own request.
			applyResponseRow(updated);
			setIsEditing(false);
			setEditValues({});
			showSuccess("Form responses updated");

			// ⚠️ `onSave()` is deliberately NOT called here, and that is a departure from the
			// brief made after reading the callers. All SIX call sites use `onSave` to CLOSE
			// this drawer, so calling it would slam the modal shut on the practitioner the
			// instant their correction landed — they would never see the updated answers, the
			// "Last edited by …" line or the new revision. Nothing is lost by omitting it:
			// F4 leaves `assigned_forms` completely untouched (status included), so not one
			// column any caller's list renders has changed. `handleSave` — which DOES change
			// the assignment — still calls `onSave()` exactly as before.
			await loadRevisions(updated);
		} catch (error: any) {
			console.error("Failed to update form response:", error);
			showError(error.response?.data?.message || "Failed to update responses. Please try again.");
		} finally {
			setIsSavingResponse(false);
		}
	}, [assignedForm.id, buildResponsePayload, applyResponseRow, loadRevisions, showError, showSuccess]);

	const handleSaveResponse = useCallback(() => {
		if (!responseValid) return;
		Modal.confirm({
			title: "Replace the submitted answers?",
			icon: <ExclamationCircleOutlined />,
			content:
				"This will replace the submitted answers. The original responses are kept in the "
				+ "revision history and your name is recorded against the change.",
			okText: "Replace answers",
			cancelText: "Cancel",
			// An irreversible clinical write must not be confirmable by a stray click on the
			// backdrop or a reflexive Escape.
			maskClosable: false,
			keyboard: false,
			onOk: () => performSaveResponse(),
		});
	}, [responseValid, performSaveResponse]);

	// ─── Provenance ───────────────────────────────────────────────────────────────
	//
	// The API gives IDS, not names (`submitted_by_user_id` / `last_edited_by_user_id` are the
	// JWT `sub`), and no endpoint here resolves them. So a name is shown only where one is
	// genuinely known — the signed-in user, or a practitioner already attached to this
	// assignment — and otherwise the role is stated plainly. Nothing is guessed.
	const nameForUser = useCallback((userId?: UserUuid | null): string | null => {
		if (!userId) return null;
		if (currentUser?.id === userId) return "you";
		const known = [assignedForm.practitioner, assignedForm.assigned_to_practitioner]
			.find((p) => p && (p as any).id === userId);
		if (known) {
			const name = `${(known as any).first_name ?? ""} ${(known as any).last_name ?? ""}`.trim();
			if (name) return name;
		}
		return null;
	}, [currentUser, assignedForm.practitioner, assignedForm.assigned_to_practitioner]);

	const patientName = assignedForm.patient
		? `${assignedForm.patient.first_name ?? ""} ${assignedForm.patient.last_name ?? ""}`.trim()
		: "";

	const provenanceText = useMemo(() => {
		if (!formResponse) return null;

		const role = formResponse.submitted_by_role;
		let who: string | null = null;
		if (role === "patient") who = patientName || "the patient";
		else if (role === "practitioner") who = nameForUser(formResponse.submitted_by_user_id) ?? "a practitioner";
		else who = nameForUser(formResponse.submitted_by_user_id);

		// A NULL `submitted_via` means the row predates F1. Say "Submitted" and stop —
		// naming a method we do not know would be a fabricated provenance claim.
		const via = formResponse.submitted_via ? SUBMITTED_VIA_LABELS[formResponse.submitted_via] : null;

		let submitted = "Submitted";
		if (who) submitted += ` by ${who}`;
		if (via) submitted += ` via ${via}`;
		if (formResponse.created_at) submitted += ` on ${format(new Date(formResponse.created_at), "dd/MM/yyyy HH:mm")}`;

		const count = formResponse.edit_count ?? 0;
		if (count < 1) return submitted;

		const editor = nameForUser(formResponse.last_edited_by_user_id) ?? "a practitioner";
		let edited = `Last edited by ${editor}`;
		if (formResponse.last_edited_at) edited += ` on ${format(new Date(formResponse.last_edited_at), "dd/MM/yyyy HH:mm")}`;
		edited += ` — ${count} revision${count === 1 ? "" : "s"}`;

		return `${submitted} · ${edited}`;
	}, [formResponse, patientName, nameForUser]);

	// ─── PDF Export ───────────────────────────────────────────────────────────────
	const exportToPDF = async () => {
		if (!formResponse || !assignedForm.patient) return;
		setIsExporting(true);
		try {
			const { default: jsPDF } = await import("jspdf");
			const doc = new jsPDF();
			const pageWidth = doc.internal.pageSize.getWidth();
			const pageHeight = doc.internal.pageSize.getHeight();
			const margin = 20;
			const contentWidth = pageWidth - margin * 2;
			let y = margin;

			const addText = (text: string, x: number, yPos: number, maxW: number, size = 12, bold = false) => {
				doc.setFontSize(size);
				doc.setFont("helvetica", bold ? "bold" : "normal");
				const lines = doc.splitTextToSize(text, maxW);
				doc.text(lines, x, yPos);
				return yPos + lines.length * (size * 0.4);
			};

			const checkPage = () => {
				if (y > pageHeight - 40) { doc.addPage(); y = margin; }
			};

			y = addText("Form Response Report", margin, y, contentWidth, 18, true);
			y += 10;
			y = addText(`Patient: ${assignedForm.patient.first_name} ${assignedForm.patient.last_name}`, margin, y, contentWidth, 12, true);
			y = addText(`Email: ${assignedForm.patient.email}`, margin, y, contentWidth, 10);
			y = addText(`Form: ${assignedForm.form?.title || "N/A"}`, margin, y, contentWidth, 10);
			y = addText(`Date: ${format(new Date(formResponse.created_at), "PPP")}`, margin, y, contentWidth, 10);
			y += 15;
			doc.line(margin, y, pageWidth - margin, y);
			y += 15;

			for (const field of formResponse.structure || []) {
				checkPage();
				const f = field as RichFieldResponse;

				switch (normaliseFieldType(f.type)) {
					case "heading":
						y = addText(f.question || "", margin, y, contentWidth, 16, true);
						y += 10;
						break;
					case "paragraph":
						y = addText(f.question || "", margin, y, contentWidth, 12);
						y += 8;
						break;
					// Multi select — one bullet per selected option. `multiselect` joins this
					// branch rather than falling through to `default:`, where an array was
					// printed via toString() as a single comma-joined line.
					case "multi_select":
					case "multiselect": {
						y = addText(`${f.question || ""}:`, margin, y, contentWidth, 12, true);
						const vals = toMultiValue(f.response) as string[];
						if (vals.length === 0) {
							y = addText("No selection", margin + 10, y, contentWidth - 10, 11);
						} else {
							vals.forEach((v: string) => { y = addText(`• ${v}`, margin + 10, y, contentWidth - 10, 11); checkPage(); });
						}
						y += 8;
						break;
					}
					// Single select — one option. Coerced the same way the on-screen renderer
					// does, so the PDF and the screen cannot disagree about a submission.
					case "single_select":
					case "select": {
						y = addText(`${f.question || ""}:`, margin, y, contentWidth, 12, true);
						const val = toSingleValue(f.response);
						y = addText(val !== undefined && val !== null && val !== "" ? String(val) : "No selection", margin + 10, y, contentWidth - 10, 11);
						y += 8;
						break;
					}
					case "date":
					case "time":
					case "datetime": {
						y = addText(`${f.question || ""}:`, margin, y, contentWidth, 12, true);
						let dStr = "No date selected";
						if (f.response) {
							try {
								const d = new Date(f.response);
								dStr = f.type === "date" ? format(d, "PPP") : f.type === "time" ? format(d, "p") : format(d, "PPP p");
							} catch { dStr = f.response.toString(); }
						}
						y = addText(dStr, margin + 10, y, contentWidth - 10, 11);
						y += 8;
						break;
					}
					case "file":
						y = addText(`${f.question || ""}:`, margin, y, contentWidth, 12, true);
						y = addText("File uploaded (see system for details)", margin + 10, y, contentWidth - 10, 11);
						y += 8;
						break;
					case "signature":
						y = addText(`${f.question || ""}:`, margin, y, contentWidth, 12, true);
						y = addText("Signature provided (see system for details)", margin + 10, y, contentWidth - 10, 11);
						y += 8;
						break;
					default:
						y = addText(`${f.question || ""}:`, margin, y, contentWidth, 12, true);
						y = addText(f.response?.toString() || "No response", margin + 10, y, contentWidth - 10, 11);
						y += 8;
						break;
				}
			}

			const pageCount = (doc.internal as any).pages.length - 1;
			for (let i = 1; i <= pageCount; i++) {
				doc.setPage(i);
				doc.setFontSize(8);
				doc.setFont("helvetica", "normal");
				doc.text(`Page ${i} of ${pageCount}`, pageWidth - margin - 30, pageHeight - 10);
				doc.text(`Generated on ${format(new Date(), "PPP p")}`, margin, pageHeight - 10);
			}

			const filename = `Form_Response_${assignedForm.patient.last_name}_${assignedForm.patient.first_name}_${format(new Date(), "yyyy-MM-dd")}.pdf`;
			doc.save(filename);
			showSuccess("PDF exported successfully");
		} catch (error) {
			console.error("Error exporting PDF:", error);
			showError("Failed to export PDF");
		} finally {
			setIsExporting(false);
		}
	};

	// ─── Layout ───────────────────────────────────────────────────────────────────
	const renderFormDetails = () => (
		<Space direction="vertical" size="large" style={{ width: "100%" }}>
			{assignedForm.patient && (
				<SectionCard title="Patient Information">
					<Row gutter={[16, 16]}>
						<Col xs={24} md={8}>
							<Text type="secondary" style={{ display: "block", fontSize: 12, marginBottom: 4 }}>Patient Name</Text>
							<div style={{ fontSize: 14, fontWeight: 500 }}>
								{assignedForm.patient.first_name} {assignedForm.patient.last_name}
							</div>
						</Col>
						<Col xs={24} md={8}>
							<Text type="secondary" style={{ display: "block", fontSize: 12, marginBottom: 4 }}>Patient ID</Text>
							<div style={{ fontSize: 14 }}>{assignedForm.patient.id}</div>
						</Col>
						<Col xs={24} md={8}>
							<Text type="secondary" style={{ display: "block", fontSize: 12, marginBottom: 4 }}>Date of Birth</Text>
							<div style={{ fontSize: 14 }}>
								{assignedForm.patient.date_of_birth ? format(new Date(assignedForm.patient.date_of_birth), "dd/MM/yyyy") : "N/A"}
							</div>
						</Col>
					</Row>
				</SectionCard>
			)}

			{assignedForm.form && (
				<SectionCard title="Form Information">
					<Row gutter={[16, 16]}>
						<Col xs={24} md={12}>
							<Text type="secondary" style={{ display: "block", fontSize: 12, marginBottom: 4 }}>Form Title</Text>
							<div style={{ fontSize: 14, fontWeight: 500 }}>{assignedForm.form.title}</div>
						</Col>
						<Col xs={24} md={6}>
							<Text type="secondary" style={{ display: "block", fontSize: 12, marginBottom: 4 }}>Current Status</Text>
							<StatusTag status={assignedForm.status} label={assignedForm.status} />
						</Col>
						<Col xs={24} md={8}>
							<Text type="secondary" style={{ display: "block", fontSize: 12, marginBottom: 4 }}>Assigned Date</Text>
							<div style={{ fontSize: 14 }}>
								{assignedForm.created_at ? format(new Date(assignedForm.created_at), "dd/MM/yyyy HH:mm") : "N/A"}
							</div>
						</Col>
						{assignedForm.practitioner && (
							<Col xs={24} md={8}>
								<Text type="secondary" style={{ display: "block", fontSize: 12, marginBottom: 4 }}>Assigned By</Text>
								<div style={{ fontSize: 14 }}>
									{assignedForm.practitioner.first_name} {assignedForm.practitioner.last_name}
								</div>
							</Col>
						)}
						{assignedForm.reviewed_at && (
							<Col xs={24} md={8}>
								<Text type="secondary" style={{ display: "block", fontSize: 12, marginBottom: 4 }}>Previously Reviewed</Text>
								<div style={{ fontSize: 14 }}>{format(new Date(assignedForm.reviewed_at), "dd/MM/yyyy HH:mm")}</div>
							</Col>
						)}
					</Row>
					{assignedForm.notes && (
						<div style={{ marginTop: 16 }}>
							<Text type="secondary" style={{ display: "block", fontSize: 12, marginBottom: 4 }}>Existing Notes</Text>
							<div style={{ padding: 12, borderRadius: 4, fontSize: 14, whiteSpace: "pre-wrap", background: "var(--mg-bg-subtle)", border: "1px solid var(--mg-border)" }}>
								{assignedForm.notes}
							</div>
						</div>
					)}
				</SectionCard>
			)}

			{formResponse && (
				<SectionCard title={isEditing ? "Form Response — editing answers" : "Form Response"}>
					<Space direction="vertical" size="middle" style={{ width: "100%" }}>
						{provenanceText && (
							<Text data-testid="review-form-provenance" type="secondary" style={{ fontSize: 12, display: "block" }}>
								{provenanceText}
							</Text>
						)}

						{isEditing && (
							<Alert
								type="warning"
								showIcon
								message="You are editing the patient's submitted answers"
								description={
									"Saving replaces them. The originals are kept in the revision history and your name is "
									+ "recorded against the change. This does not change the form's status, and it saves "
									+ "separately from the review status and notes below."
									+ (signatureFieldIds.size > 0
										? " The patient's signature is shown but cannot be changed here — a practitioner "
										+ "must never alter a signature made on a patient's behalf."
										: "")
								}
							/>
						)}

						{isEditing && !responseValid && (
							// sectionMode is "flat", where FormRenderer suppresses its own per-field
							// error text (errorsEnabled = !readOnly && sectionMode !== 'flat'), so
							// invalidity has to be surfaced from out here or it is invisible.
							<Alert
								type="error"
								showIcon
								message="A required answer is empty"
								description="Fill every required field before saving. Saving is blocked until then."
							/>
						)}

						{formResponse.structure && formResponse.structure.length > 0 && (
							<UnifiedFormRenderer
								structure={isEditing ? editStructure : parsedFormStructure}
								useSections={useSections}
								values={isEditing ? editValues : responseMap}
								onValuesChange={isEditing ? setEditValues : () => {}}
								onValidityChange={isEditing ? setResponseValid : undefined}
								readOnly={!isEditing}
								readOnlyFieldIds={isEditing ? signatureFieldIds : undefined}
								sectionMode="flat"
							/>
						)}
					</Space>
				</SectionCard>
			)}

			{revisions.length > 0 && (
				<SectionCard title="Revision History">
					<div data-testid="review-form-revisions">
						<Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: 8 }}>
							Answers that were replaced by an edit, newest first. Expand one to see it as it stood.
						</Text>
						<Collapse
							items={revisions.map((r) => ({
								key: String(r.id),
								label: `Revision ${r.revision_number} · ${nameForUser(r.revised_by_user_id) ?? "a practitioner"}`
									+ (r.created_at ? ` · ${format(new Date(r.created_at), "dd/MM/yyyy HH:mm")}` : ""),
								children: (
									// The shared renderer, read-only — deliberately NOT a diff. A
									// side-by-side of arbitrary form JSON is its own project and is not
									// what makes a correction defensible; the superseded answers are.
									<UnifiedFormRenderer
										structure={parsedFormStructure}
										useSections={useSections}
										values={resolveResponseMap(r.response)}
										onValuesChange={() => {}}
										readOnly
										sectionMode="flat"
									/>
								),
							}))}
						/>
					</div>
				</SectionCard>
			)}

			<SectionCard title="Review & Update">
				<Space direction="vertical" style={{ width: "100%" }} size="middle">
					<div>
						<label style={{ display: "block", marginBottom: 8, fontWeight: 500 }}>Update Status</label>
						<Select
							data-testid="review-form-status-select"
							value={newStatus}
							onChange={setNewStatus}
							options={statusOptions}
							style={{ width: "100%" }}
						/>
					</div>
					<div>
						<label style={{ display: "block", marginBottom: 8, fontWeight: 500 }}>Review Notes</label>
						<TextArea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Add your review notes here..." rows={4} />
						<Text type="secondary" style={{ fontSize: 12, marginTop: 4, display: "block" }}>
							These notes will be appended to any existing notes
						</Text>
					</div>

					{/* ─── Reopen ──────────────────────────────────────────────────────────────
					    The move the picker above no longer offers, done on purpose instead of by
					    accident. `Completed -> Pending` and `Reviewed -> Pending` are the only two
					    transitions that discard a submission, and they used to be indistinguishable
					    from a notes edit; S3 gives them their own route and their own
					    `form.reopened` audit event, and this is its affordance.

					    It sits with the status control it replaces, and only outside edit mode: it
					    refreshes through `onSave()`, which the call sites use to CLOSE this drawer,
					    and mid-edit that would throw away an unsaved answer correction. */}
					{!isEditing && canReopen(assignedForm.status) && (
						<div>
							<Button
								data-testid="review-form-reopen"
								icon={<UndoOutlined />}
								loading={isReopening}
								disabled={isSubmitting || isSavingResponse}
								onClick={handleReopen}
							>
								Reopen form
							</Button>
							<Text type="secondary" style={{ fontSize: 12, marginTop: 4, display: "block" }}>
								Returns the form to Pending so it can be submitted again. The submitted answers are
								kept, and the reopen is recorded against your name.
							</Text>
						</div>
					)}

					{/* While editing, the header's "Save & Update" is withdrawn so only ONE save is
					    ever offered per thing being saved. This one moves down here, beside the two
					    controls it writes, and says so in its label — the answer save is in the
					    footer and says "answer". Outside edit mode nothing moves: the header button
					    stays exactly where it has always been. */}
					{isEditing && (
						<div>
							<Button
								data-testid="review-form-status-save"
								icon={<SaveOutlined />}
								loading={isSubmitting}
								disabled={isSavingResponse}
								onClick={handleSave}
							>
								Save status &amp; notes
							</Button>
							<Text type="secondary" style={{ fontSize: 12, marginTop: 4, display: "block" }}>
								Saves only the status and notes above — not the answers you are editing.
							</Text>
						</div>
					)}
				</Space>
			</SectionCard>
		</Space>
	);

	return (
		<DetailDrawer
			open={isOpen}
			onClose={onClose}
			width="85vw"
			avatar={
				<Avatar size={44} icon={<FileTextOutlined />} style={{ background: "var(--mg-primary-bg)", color: "var(--mg-primary)" }} />
			}
			title="Review Assigned Form"
			subtitle={
				assignedForm.patient && assignedForm.form
					? `${assignedForm.patient.first_name} ${assignedForm.patient.last_name} · ${assignedForm.form.title}`
					: assignedForm.form?.title
			}
			headerActions={
				isEditing ? (
					<Tag color="warning">Editing answers</Tag>
				) : (
					<Space>
						{/* Secondary, never primary: editing submitted clinical data should take a
						    deliberate click. Shown to EVERY authenticated practitioner — F4 adds no
						    ownership check, because `assigned_forms.practitioner_id` is nullable for
						    admin-pushed global forms and gating on it would lock those out. */}
						{formResponse && formResponse.structure && formResponse.structure.length > 0 && (
							<Button
								data-testid="review-form-edit-toggle"
								icon={<EditOutlined />}
								onClick={handleEnterEdit}
								disabled={isSubmitting}
							>
								Edit responses
							</Button>
						)}
						<Button type="primary" icon={<SaveOutlined />} loading={isSubmitting} onClick={handleSave}>
							Save & Update
						</Button>
					</Space>
				)
			}
			footer={
				isEditing ? (
					<Space>
						<Button data-testid="review-form-edit-cancel" onClick={handleCancelEdit} disabled={isSavingResponse}>
							Cancel
						</Button>
						<Button
							data-testid="review-form-edit-save"
							type="primary"
							icon={<SaveOutlined />}
							loading={isSavingResponse}
							disabled={!responseValid}
							onClick={handleSaveResponse}
						>
							Save answer changes
						</Button>
					</Space>
				) : (
					<Space>
						{formResponse && formResponse.structure && formResponse.structure.length > 0 && (
							<Button icon={<FilePdfOutlined />} onClick={exportToPDF} loading={isExporting} disabled={isExporting || isSubmitting}>
								Export PDF
							</Button>
						)}
						{assignedForm.reviewed_at && (
							<StatusTag status="reviewed" label={`Reviewed: ${format(new Date(assignedForm.reviewed_at), "dd/MM/yyyy")}`} />
						)}
					</Space>
				)
			}
		>
			{renderFormDetails()}
		</DetailDrawer>
	);
};

export default ReviewFormModal;